import { Injectable } from '@angular/core';
import {
  CredentialsAndIdentityIdProvider,
  CredentialsAndIdentityId,
  GetCredentialsOptions
} from 'aws-amplify/auth';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Cache } from 'aws-amplify/utils';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { CognitoIdentity } from '@aws-sdk/client-cognito-identity';
import { environment } from '@env/environment';
import { lastValueFrom } from 'rxjs';
import { OidcConfigIds } from '@common/models/user-management.model';

/**
 * Service to get the Cognito credentials
 */
@Injectable({
  providedIn: 'root'
})
export class CognitoServiceService implements CredentialsAndIdentityIdProvider {
  amplifyIdp: string;

  cognitoidentity = new CognitoIdentity({
    region: 'us-east-1'
  });

  constructor(
    private readonly oidcSecurityService: OidcSecurityService,
  ) {
    this.amplifyIdp = OidcConfigIds.XGS;
  }

  /**
   * Implement this to get the credentials and identityId. This can be called when making a request to the federation service.
   * @param getCredentialsOptions The options to pass to the getCredentials method
   * @returns The credentials and identityId
   */
  async getCredentialsAndIdentityId(
    getCredentialsOptions: GetCredentialsOptions
  ): Promise<CredentialsAndIdentityId | undefined> {
    try {
      const value: CredentialsAndIdentityId = await Cache.getItem(
        'federatedInfo'
      );

      if (value && this.verifyExpiration(value.credentials?.expiration)) {
        // Cache deserializes Date as string; convert back to Date for aws-amplify
        if (value.credentials?.expiration && !(value.credentials.expiration instanceof Date)) {
          value.credentials.expiration = new Date(value.credentials.expiration);
        }
        return value;
      }

      const token = await this.getToken();

      const getIdResult = await this.cognitoidentity.getId({
        // Get the identityPoolId from config
        IdentityPoolId: environment.identityPoolId,
        Logins: { [this.getIdentityProviderName()]: token }
      });

      const cognitoCredentialsResult =
        await this.cognitoidentity.getCredentialsForIdentity({
          IdentityId: getIdResult.IdentityId,
          Logins: {
            [this.getIdentityProviderName()]: token
          }
        });

      const credentials: CredentialsAndIdentityId = {
        credentials: {
          accessKeyId: cognitoCredentialsResult.Credentials?.AccessKeyId,
          secretAccessKey: cognitoCredentialsResult.Credentials?.SecretKey,
          sessionToken: cognitoCredentialsResult.Credentials?.SessionToken,
          expiration: cognitoCredentialsResult.Credentials?.Expiration
        },
        identityId: getIdResult.IdentityId
      };

      if (token) {
        await Cache.setItem('federatedInfo', credentials, {
          expires: jwtDecode<JwtPayload>(token).exp * 1000
        });
      }

      return credentials;
    } catch (error) {

    }
  }

  /**
   * Implement this to clear any cached credentials and identityId. This can be called when signing out of the federation service.
   */
  clearCredentialsAndIdentityId(): void {}

  /**
   * Returns the identity provider name that will be used to get the token
   * @returns The identity provider name
   */
  getIdentityProviderName(): string {
    return environment.xgsIdp;
  }

  /**
   * Set the identity provider name that will be used to get the token
   * @param idp The identity provider name
   */
  setAmplifyIdp(idp: string): void {
    this.amplifyIdp = idp;
  }

  /**
   * Get the token from the oidc security service
   * @returns The token
   */
  async getToken(): Promise<string> {
    const source$ = this.oidcSecurityService.getAccessToken(this.amplifyIdp);

    return lastValueFrom(source$);
  }

  /**
   * Verify the expiration of the token
   * @param expiration The expiration date
   * @returns True if the token is not expired, false otherwise
   */
  verifyExpiration(expiration: Date): boolean {
    // Refresh a few minutes early so pre-signed URLs, whose lifetime is capped
    // at the credentials' remaining lifetime, aren't signed with near-expired
    // credentials.
    const refreshBufferMs = 5 * 60 * 1000;
    const currentTime = new Date().getTime();
    const expirationTime = new Date(expiration).getTime();
    return currentTime + refreshBufferMs < expirationTime;
  }
}
